<script setup>
import NMenu from '/app/components/NMenu.vue';
import { ref, computed, watch, nextTick, onBeforeUpdate, useTemplateRef } from 'vue';
import NIcon from '/app/components/NIcon.vue';
import NLoading from '/app/components/NLoading.vue';
import NInput from '/app/components/form/Input.vue';

const props = defineProps({
    id: String,
    name: String,
    label: String,
    hint: String,
    modelValue: [String, Number],
    placeholder: String,
    disabled: Boolean,
    clearable: Boolean,
    loading: Boolean,
    prependIcon: String,
    noFilter: Boolean,
    autofocus: Boolean,
    returnObject: Boolean,
    openOnClick: {
        type: Boolean,
        default: false,
    },
    filter: {
        type: Function,
        default: (item, queryText, itemText) =>
            itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1,
    },
    itemText: {
        type: [String, Function],
        default: 'name',
    },
    itemValue: {
        type: [String, Function],
        default: 'value',
    },
    items: {
        type: Array,
        default: () => [],
    },
    queryInput: String,
    itemsType: {
        type: String,
        default: 'dropdown',
        validator(value) {
            return ['dropdown', 'list'].indexOf(value) > -1;
        },
    },
});

const emit = defineEmits(['update:modelValue', 'update:query-input']);

const dropdown = useTemplateRef('dropdown');
const input = useTemplateRef('input');

const query = ref('');
const highlightedIndex = ref(-1);

// `ref="items"` inside v-for collects DOM elements into an array. The function-ref
// pattern is the standard <script setup> idiom: reset the array before each render
// (onBeforeUpdate) so stale element references don't accumulate across re-renders,
// then collect fresh ones via setItemRef.
const itemRefs = ref([]);
const setItemRef = (el) => {
    if (el) itemRefs.value.push(el);
};
onBeforeUpdate(() => {
    itemRefs.value = [];
});

function getText(item) {
    if (!item) return;
    return typeof props.itemText === 'function' ? props.itemText(item) : item[props.itemText];
}

function getValue(item) {
    if (!item) return;
    if (props.returnObject) return item;
    return typeof props.itemValue === 'function' ? props.itemValue(item) : item[props.itemValue];
}

const availabeItems = computed(() =>
    props.items.filter(
        props.noFilter
            ? () => true
            : (item) => props.filter(item, query.value, getText(item)),
    ),
);

function scrollToHighlight() {
    if (highlightedIndex.value < 0) return;

    nextTick(() => {
        const el = itemRefs.value[highlightedIndex.value];
        el && el.scrollIntoView({ block: 'nearest' });
    });
}

function highlight(index) {
    dropdown.value?.open();
    highlightedIndex.value = index;

    if (itemRefs.value && itemRefs.value.length) {
        scrollToHighlight();
    }
}

function highlightNext() {
    const next = highlightedIndex.value + 1;
    const index = next >= availabeItems.value.length ? 0 : next;
    highlight(index);
}

function highlightPrev() {
    const prev = highlightedIndex.value - 1;
    const index = prev < 0 ? availabeItems.value.length - 1 : prev;
    highlight(index);
}

function select(index) {
    highlight(index);

    const item = availabeItems.value[index];

    if (item) {
        query.value = getText(item);
        emit('update:modelValue', getValue(item));
        dropdown.value?.close();
    }
}

function clear() {
    dropdown.value?.close();
    input.value?.focus();

    query.value = null;
    emit('update:modelValue', null);
}

function onQueryInput(value) {
    query.value = value;
    emit('update:query-input', value);
}

function show() {
    dropdown.value?.open();
}

watch(
    () => props.queryInput,
    (value) => {
        query.value = value;
    },
    { immediate: true },
);

defineExpose({ show, clear });
</script>

<template>
    <NMenu ref="dropdown" :floating="itemsType ? itemsType === 'dropdown' : true" @open="scrollToHighlight">
        <template #activator="{ open, close, active }">
            <NInput
                :modelValue="query"
                :label="label"
                :hint="hint"
                :placeholder="placeholder"
                :name="name"
                :id="id"
                :disabled="disabled"
                :autofocus="autofocus"
                :min="4"
                :max="45"
                autocomplete="off"
                ref="input"
                maxlength="40"
                class="w-full"
                @update:modelValue="onQueryInput"
                @focus="(openOnClick || availabeItems.length > 0) && open()"
                @click="openOnClick && open()"
                @keydown.tab="(e) => active && (e.preventDefault(), highlightNext())"
                @keydown.up.prevent="highlightPrev"
                @keydown.down.prevent="highlightNext"
                @keydown.enter.prevent="active && select(highlightedIndex)"
            >
                <template v-if="prependIcon" #prepend>
                    <NIcon :as="prependIcon" class="mr-2"/>
                </template>
                <template #append>
                    <NLoading v-if="loading" class="-mr-2" active />

                    <button aria-label="button" v-else-if="clearable" v-tooltip="'Clear'" type="button" @click="clear" class="cursor-pointer flex items-center justify-center h-6 w-6 text-dust-600 hover:bg-dust-100 hover:text-dust-700 rounded-full -mr-1">
                        <NIcon as="close" />
                    </button>
                </template>
            </NInput>
        </template>

        <NLoading v-if="loading" loading class="py-20 flex justify-center w-full" />

        <ul v-else-if="availabeItems.length" class="flex flex-col justify-start w-full p-0 m-0 snap snap-y overflow-y-auto max-h-60 sb-snug sb-dust-200">
            <template v-for="(item, index) of availabeItems" :key="item.id">
                <li :ref="setItemRef" :class="{ 'bg-dust-100': highlightedIndex === index }" class="cursor-pointer hover:bg-dust-100 hover:text-dust-800" @click="select(index)">
                    <slot name="item" :index="index" :item="item">
                        <span class="text-sm px-3 py-1 block">{{ item[itemText] }}</span>
                    </slot>
                </li>
            </template>
        </ul>

        <div v-else class="py-10 px-4 w-full text-center">
            <slot name="no-data">
                No items were found!
            </slot>
        </div>
    </NMenu>
</template>
